import PropTypes from 'prop-types'
import React, { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { makeStyles } from '@material-ui/core'
import Collapse from '@material-ui/core/Collapse'
import CodeTag from '../../../../../../atoms/CodeTag'
import FlexRow from '../../../../../../molecules/FlexRow'
import Loading from '../../../../../../molecules/Loading'
import FadeIn from '../../../../../../molecules/Transitions/FadeIn'
import { useFormattingContext } from '../../../../../../organisms/FormattingProvider/FormattingContext'
import SydButton from '../../../../../../commonDesign/Button'
import { abundanceDesigner } from './adminPlaygroundApp'

const useStyles = makeStyles(() => ({
  content: {
    '& p': {
      margin: 0
    },
    '& * + p': {
      marginTop: '0.5em'
    }
  },
  designTrailContent: {
    '& h2, & h3, & h4': {
      margin: '0.5em 0 0 0.25em'
    },
    '& h2': {
      fontSize: '0.875rem'
    },
    '& h3': {
      fontSize: '0.75rem'
    },
    '& p, & ul': {
      margin: 0,
      fontSize: '0.75rem'
    },
    '& * + p': {
      marginTop: '0.5em'
    }
  },
  designTrail: {
    background: 'var(--color-neutral-100)',
    fontSize: '0.875rem',
    borderRadius: 'var(--border-radius-md)',
    padding: 'var(--dim-3)'
  }
}))

function ChatMessage ({ message, onJsxAttachmentClick }) {
  const classes = useStyles()
  abundanceDesigner.useGenerationPolling(message)
  const { formatter } = useFormattingContext()

  const className = message.actor === 'user'
    ? 'bg-neutral-050 p-sm shadow-md text-contrast-neutral-050 radius-sm flex flex-col gap-sm'
    : 'p-sm radius-sm flex flex-col gap-sm'

  const [showDesignTrail, setShowDesignTrail] = useState(false)

  if (!message) return null

  return (
    <div>
      <FlexRow className='justify-between' style={{ borderBottom: message?.actor === 'user' ? undefined : 'var(--border-neutral-thin)' }}>
        <span style={{ color: message?.actor === 'user' ? undefined : 'var(--color-ai-600)' }}>{message?.actor === 'user' ? 'You' : 'Abundance Designer'}</span>
        <span>{formatter(message?.createdDate, '&h:mm a')}</span>
      </FlexRow>
      <FadeIn className={className}>
        {message.isGenerating ? (
          <FlexRow>
            <span><Loading size={16} /></span>
            <span>{message.generationStatus}</span>
          </FlexRow>
        ) : message.generationStatus === 'Failed' ? (
          <FadeIn style={{ fontWeight: 'var(--font-weight-thin)' }}>
            <div className='bg-danger-100 text-contrast-danger-100 radius-md py-2 px-2'>
              There was an issue generating a response.  Please try again
            </div>
          </FadeIn>
        ) : (
          <FadeIn style={{ fontWeight: 'var(--font-weight-thin)' }}>
            <div className='relative'>
              <ReactMarkdown
                className={classes.content}
                remarkPlugins={[remarkGfm]}
              >
                {message.data.originalPrompt ?? message.message}
              </ReactMarkdown>
              {!!message?.data.designTrail && (
                <div className='flex justify-center py-2'>
                  <SydButton size='xs' variant='secondary' icon='info' onClick={() => setShowDesignTrail(!showDesignTrail)}>
                    {showDesignTrail ? 'Hide' : 'View'} Thinking Process
                  </SydButton>
                </div>
              )}
            </div>

            {message?.data.designTrail ? (
              <Collapse in={showDesignTrail}>
                <div className={classes.designTrail}>
                  <ReactMarkdown
                    className={classes.designTrailContent}
                    remarkPlugins={[remarkGfm]}
                  >
                    {message?.data.designTrail}
                  </ReactMarkdown>
                </div>
              </Collapse>
            ) : null}
          </FadeIn>
        )}
        {(message.attachments || []).map((attachment) => (
          <div
            key={attachment.threadMessageAttachmentId}
            className='opal-surface-default opal-selectable radius-sm p-xs flex flex-row gap-sm items-center'
            style={{ borderColor: 'var(--color-ai-500)' }}
            onClick={() => onJsxAttachmentClick(attachment)}
          >
            <CodeTag>{attachment.contentType}</CodeTag>
            <span>{attachment.title}</span>
          </div>
        ))}
      </FadeIn>
    </div>
  )
}

ChatMessage.propTypes = {
  message: PropTypes.shape({
    threadId: PropTypes.string,
    threadMessageId: PropTypes.string,
    actor: PropTypes.oneOf(['agent', 'user']),
    message: PropTypes.string,
    data: PropTypes.shape({
      originalPrompt: PropTypes.string,
      designTrail: PropTypes.string
    }),
    isGenerating: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    generationStatus: PropTypes.string,
    responseId: PropTypes.string,
    createdDate: PropTypes.string,
    attachments: PropTypes.array,
    explanation: PropTypes.string
  }),
  onJsxAttachmentClick: PropTypes.func
}

export default ChatMessage
